import { ApiError } from '@api/api-client';
import { type EditionResponse, updateEdition } from '@api/editions-api';
import { listSites, type SiteResponse } from '@api/sites-api';
import ConfirmationModal from '@components/confirmation-modal';
import CountryPicker from '@components/country-picker';
import FieldError from '@components/field-error';
import Modal from '@components/modal';
import YearPicker from '@components/year-picker';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { type EditionFormData, editionSchema } from '@validation/schemas';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { sileo } from 'sileo';

function siteOptionLabel(site: SiteResponse): string {
  return site.authMode === 'password' ? `PMS — ${site.name}` : `Marketplace — ${site.name}`;
}

interface EditEditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  edition: EditionResponse | null;
}

export default function EditEditionModal({
  isOpen,
  onClose,
  edition,
}: EditEditionModalProps): React.JSX.Element {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    watch,
    control,
  } = useForm<EditionFormData>({
    resolver: zodResolver(editionSchema),
    defaultValues: {
      name: '',
      year: undefined as unknown as number,
      countryId: '',
      siteId: '',
      externalId: undefined,
    },
  });

  useEffect(() => {
    if (edition) {
      reset({
        name: edition.name,
        year: edition.year,
        countryId: edition.countryId,
        siteId: edition.siteId,
        externalId: edition.externalId ?? undefined,
      });
    }
  }, [edition, reset]);

  const countryId = watch('countryId');

  const { data: sites = [] } = useQuery({
    queryKey: ['sites', countryId],
    queryFn: () => listSites(countryId),
    enabled: Boolean(countryId),
    staleTime: 60_000,
  });

  if (!edition) return <></>;

  const onValid = (): void => {
    setIsConfirmOpen(true);
  };

  const handleConfirm = async (): Promise<void> => {
    const data = getValues();

    try {
      await updateEdition(edition.id, {
        name: data.name.trim(),
        year: data.year,
        countryId: data.countryId,
        ...(data.siteId ? { siteId: data.siteId } : {}),
        externalId: data.externalId ?? null,
      });

      sileo.success({ title: 'Edition updated successfully' });
      setIsConfirmOpen(false);
      onClose();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to update edition';
      sileo.error({ title: message });
      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Edit edition"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleSubmit(onValid)()}
            >
              <Save size={16} />
              Update edition
            </button>
          </>
        }
      >
        <form className="modal-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label htmlFor="edit-edition-name" className="form-label">
              Name
            </label>
            <input
              id="edit-edition-name"
              type="text"
              className={`form-input${errors.name ? ' form-input--error' : ''}`}
              placeholder="Edition name"
              {...register('name')}
            />
            <FieldError error={errors.name} />
          </div>

          <div className="form-group">
            <label htmlFor="edit-edition-external-id" className="form-label">
              CSV ID
            </label>
            <input
              id="edit-edition-external-id"
              type="number"
              className={`form-input${errors.externalId ? ' form-input--error' : ''}`}
              placeholder="Optional"
              {...register('externalId', {
                setValueAs: (v: string) => (v === '' ? undefined : Number(v)),
              })}
            />
            <FieldError error={errors.externalId} />
          </div>

          <div className="form-group">
            <span className="form-label">Year</span>
            <Controller
              name="year"
              control={control}
              render={({ field }) => (
                <YearPicker value={field.value || null} onChange={field.onChange} />
              )}
            />
            <FieldError error={errors.year} />
          </div>

          <div className="form-group">
            <span className="form-label">Country</span>
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <CountryPicker value={field.value} onChange={field.onChange} />
              )}
            />
            <FieldError error={errors.countryId} />
          </div>

          {sites.length > 0 && (
            <div className="form-group">
              <label htmlFor="edit-edition-site" className="form-label">
                Edition type
              </label>
              <Controller
                name="siteId"
                control={control}
                render={({ field }) => (
                  <select
                    id="edit-edition-site"
                    className="form-select"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  >
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {siteOptionLabel(site)}
                      </option>
                    ))}
                  </select>
                )}
              />
              <p className="form-hint">
                Changeable only while the edition has no categories or voters. Once it does, create
                a new edition for the other site instead.
              </p>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        title="Update edition"
        message="Are you sure you want to save the changes to this edition?"
        confirmLabel="Save"
      />
    </>
  );
}
